import { uiText, useUiLanguage } from '@/i18n/interface';
import React from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';
import { ArrowLeft, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { useCurrentDojo } from '@/hooks/useCurrentDojo';
import { useTrialStatus } from '@/hooks/useTrialStatus';
import { usePremiumAccess } from '@/hooks/usePremiumAccess';

const Subscription: React.FC = () => {
  useUiLanguage();
  const { profile, isLoading: authLoading } = useAuth();
  const { dojo, loading: dojoLoading } = useCurrentDojo();
  const { hasPremiumAccess: hasAccess } = usePremiumAccess();
  const navigate = useNavigate();
  const { isOnTrial, loading: trialLoading } = useTrialStatus();

  const handleUpgradeClick = () => {
    const paymentLink = 'https://buy.stripe.com/fZu5kD06o4U37Ha8WTdAk00';
    if (profile?.email) {
      const urlWithEmail = `${paymentLink}?prefilled_email=${encodeURIComponent(profile.email)}`;
      window.open(urlWithEmail, '_blank');
    } else {
      window.open(paymentLink, '_blank');
    }

    toast({
      title: uiText("Redirected to Payment"),
      description: uiText("Complete your payment to activate your subscription"),
    });
  };

  if (authLoading || dojoLoading || trialLoading) {
    return (
      <div className="wh-app min-h-screen bg-background from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-foreground">{uiText("Loading...")}</div>
      </div>
    );
  }

  if (!profile) {
    toast({
      title: uiText("Authentication Required"),
      description: uiText("Please log in to upgrade to Pro"),
      variant: "destructive"
    });
    navigate('/login');
    return null;
  }

  if (hasAccess && !isOnTrial) {
    return (
      <div className="wh-app min-h-screen bg-background from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <div className="text-center text-foreground">
          <h1 className="text-3xl font-bold mb-4">{uiText("You already have Pro access!")}</h1>
          <p className="text-lg mb-6">{uiText("Thank you for being a valued member.")}</p>
          <Button onClick={() => navigate('/')} className="bg-purple-600 hover:bg-purple-700">
            {uiText("Go to Dashboard")}</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="wh-app min-h-screen bg-background from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md text-center text-foreground bg-card border rounded-2xl shadow-soft p-8">
        {isOnTrial && <Button variant="ghost" className="mb-4" onClick={() => navigate('/', { replace: true })}><ArrowLeft className="mr-2 h-4 w-4" />{uiText("Back to trial")}</Button>}
        <h1 className="wh-page-title mb-4">
          {uiText("Unlock Pro Features")}</h1>
        <p className="text-lg text-muted-foreground mb-6">
          {uiText("Access advanced features, more storage, and priority support.")}</p>

        <div className="border-t border-border pt-6 mt-6">
          <h2 className="text-2xl font-semibold mb-4">{uiText("Pro Subscription")}</h2>
          <p className="text-xl font-bold mb-4">{uiText("CHF 49.99 / month")}</p>

          <Button
            onClick={handleUpgradeClick}
            size="lg"
            className="w-full font-semibold py-3 text-lg transition-all duration-300 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 transform hover:scale-105 text-white"
          >
            <CreditCard className="mr-2 h-5 w-5" />
            {uiText("Upgrade Now - CHF 49.99/month")}</Button>
        </div>
      </div>
    </div>
  );
};

export default Subscription;